import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';

interface ChartRowContextType {
  rowHeights: Map<number, number>;
  updateHeight: (rowIndex: number, cardIndex: number, height: number) => void;
  removeCard: (rowIndex: number, cardIndex: number) => void;
}

const ChartRowContext = createContext<ChartRowContextType | null>(null);

interface ChartRowProviderProps {
  children: React.ReactNode;
}

export const ChartRowProvider: React.FC<ChartRowProviderProps> = ({ children }) => {
  // Map to store heights: Map<rowIndex, Map<cardIndex, height>>
  const [cardHeights, setCardHeights] = useState<Map<number, Map<number, number>>>(new Map());
  const [rowHeights, setRowHeights] = useState<Map<number, number>>(new Map());

  const updateHeight = useCallback((rowIndex: number, cardIndex: number, height: number) => {
    setCardHeights(prev => {
      const newHeights = new Map(prev);
      if (!newHeights.has(rowIndex)) {
        newHeights.set(rowIndex, new Map());
      }
      newHeights.get(rowIndex)!.set(cardIndex, height);
      return newHeights;
    });
  }, []);

  const removeCard = useCallback((rowIndex: number, cardIndex: number) => {
    setCardHeights(prev => {
      const newHeights = new Map(prev);
      if (newHeights.has(rowIndex)) {
        const rowMap = newHeights.get(rowIndex)!;
        rowMap.delete(cardIndex);
        if (rowMap.size === 0) {
          newHeights.delete(rowIndex);
        }
      }
      return newHeights;
    });
  }, []);

  // Calculate maximum height for each row whenever cardHeights changes
  useEffect(() => {
    const newRowHeights = new Map<number, number>();
    
    cardHeights.forEach((cardsInRow, rowIndex) => {
      const maxHeight = Math.max(...Array.from(cardsInRow.values()));
      newRowHeights.set(rowIndex, maxHeight);
    });

    setRowHeights(newRowHeights);
  }, [cardHeights]);

  return (
    <ChartRowContext.Provider value={{ rowHeights, updateHeight, removeCard }}>
      {children}
    </ChartRowContext.Provider>
  );
};

export const useChartRow = () => {
  const context = useContext(ChartRowContext);
  if (!context) {
    throw new Error('useChartRow must be used within a ChartRowProvider');
  }
  return context;
}; 